import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import PageTransition from './PageTransition'
import { useSession } from '../hooks/useSession'
import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications'
import { useStaffPushResync } from '../hooks/useStaffPushResync'
import { useMobile } from '../hooks/useMobile'
import Sidebar from './Sidebar'
import AppHeader from './AppHeader'
import MobileBottomNav from './MobileBottomNav'
import MobileHeader from './MobileHeader'
import SectionTabs, { tabsForPath } from './SectionTabs'
import FloatingActionButton from './FloatingActionButton'
import { CommandPalette } from './CommandPalette'
import OfflineIndicator from './OfflineIndicator'
import SessionTimeoutWarning from './SessionTimeoutWarning'
import EmailVerificationBanner from './EmailVerificationBanner'
import RouteProgressBar from './RouteProgressBar'
import QuickLog from './QuickLog'
import { QuickLogContext } from '../hooks/useQuickLog'
import { ErrorBoundary } from './ErrorBoundary'
import NoAgencyNotice from './NoAgencyNotice'
import { showsAgencyData } from '../utils/agencyScope'
import styles from './AppLayout.module.css'

export default function AppLayout() {
  const { user } = useSession()
  const isMobile = useMobile()
  const { pathname } = useLocation()
  const sectionTabs = tabsForPath(pathname)
  // A superadmin reaches here with no agency_id. Agency-scoped pages would
  // query on nothing and render an empty book; they say so instead.
  const hasAgencyData = showsAgencyData(user?.agency_id, pathname)
  const [quickLogOpen, setQuickLogOpen] = useState(false)
  useRealtimeNotifications(user?.id ?? null, user?.agency_id ?? null)
  useStaffPushResync(user?.id ?? null)

  // ⌘L / Ctrl+L → Quick Log
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === 'l') {
        e.preventDefault()
        setQuickLogOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', handle)
    return () => document.removeEventListener('keydown', handle)
  }, [])

  return (
    <QuickLogContext.Provider value={{ open: () => setQuickLogOpen(true) }}>
    <div className={styles.layout}>
      <RouteProgressBar />
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className={styles.skipLink}
        tabIndex={0}
      >
        Skip to main content
      </a>
      {!isMobile && <Sidebar />}
      {/* Header + main stack vertically beside the rail. They must NOT be flex
          siblings of the rail, or the header competes with main for width. */}
      <div className={styles.contentColumn}>
        <EmailVerificationBanner />
        {isMobile ? <MobileHeader /> : <AppHeader />}
        <main
        id="main-content"
        className={styles.mainContent}
        role="main"
        aria-label="Main content"
        tabIndex={-1}
      >
        {sectionTabs && hasAgencyData && <SectionTabs tabs={sectionTabs} />}
        <ErrorBoundary>
          {hasAgencyData
            ? <PageTransition><Outlet /></PageTransition>
            : <NoAgencyNotice pathname={pathname} />}
        </ErrorBoundary>
        </main>
      </div>
      {isMobile && <MobileBottomNav />}
      {!isMobile && <FloatingActionButton />}
      <CommandPalette />
      <OfflineIndicator />
      <SessionTimeoutWarning />
      <QuickLog isOpen={quickLogOpen} onClose={() => setQuickLogOpen(false)} />
    </div>
    </QuickLogContext.Provider>
  )
}
